import AutoSuggest, {
    type AutoSuggestInputProps,
    type AutoSuggestSuggestion,
    type SelectedSuggestion,
} from '@rio-cloud/rio-uikit/AutoSuggest';
import first from 'lodash/fp/first';
import isEmpty from 'lodash/fp/isEmpty';
import last from 'lodash/fp/last';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { useAppSelector } from '../../configuration/setup/hooks';
import {
    selectIsAutomatedPlanningMode,
    selectIsWaypointInRange,
    selectOrderedWaypoints,
} from '../../store/search/searchSelectors';
import {
    type SuggestionAddress,
    validWaypointPredicate,
    type Waypoint,
    WaypointType,
} from '../../store/search/searchSlice';
import { selectCurrentTour } from '../../store/tour/tourSelectors';
import { changeBoundingBox } from '../../store/widget/actions/changeBoundingBox';

export type Suggestion = SuggestionAddress & { label: string; type: string; header: boolean };

export interface WaypointInputProps {
    waypoint: Waypoint;
    suggestedAddresses: SuggestionAddress[];
    onInputValueChanged: (waypointId: number, value: string) => void;
    onSuggestionSelected: (waypointId: number, suggestion: SuggestionAddress) => void;
    onInputCleared: (waypointId: number) => void;
    onRemove: () => void;
    allowRemove: boolean;
    icon: string;
}

const mapSuggestionIconType = (addressType: string) => {
    switch (addressType) {
        case 'customer':
            return 'pushpin';
        case 'workshop':
            return 'workshop';
        case 'circle':
        case 'polygon':
            return 'geofence';
        case WaypointType.VEHICLE_LOCATION:
            return 'truck';
        case 'charging-station':
            return 'filling-e-station';
        default:
            return 'map-marker';
    }
};

const mapSuggestionTitle = (addressType: string) => {
    switch (addressType) {
        case 'customer':
            return 'intl-msg:smartRoute.generic.customers';
        case 'workshop':
            return 'intl-msg:smartRoute.generic.workshops';
        case 'circle':
        case 'polygon':
            return 'intl-msg:smartRoute.generic.geofences';
        case WaypointType.VEHICLE_LOCATION:
            return 'intl-msg:smartRoute.generic.vehicle';
        case 'charging-station':
            return 'intl-msg:smartRoute.chargingStation';
        default:
            return 'intl-msg:smartRoute.generic.address';
    }
};

const getFallbackText = (waypoint: Waypoint) => {
    if (waypoint.coordinates) {
        return `${waypoint.coordinates?.lat}, ${waypoint.coordinates?.lng}`;
    }
    return '';
};

export const addSuggestionHeaders = (suggestions: Partial<Suggestion>[], intl: IntlShape): Partial<Suggestion>[] => {
    let previousCategory = '';
    let headerIndex = 0;
    return suggestions.reduce((acc, curr, index) => {
        const currCategory = mapSuggestionTitle(curr.resultType ?? '');
        if (previousCategory !== currCategory) {
            previousCategory = currCategory;
            acc.push({
                label: intl.formatMessage({ id: currCategory }),
                header: true,
                key: `header-${headerIndex++}`,
            });
        }
        acc.push({
            ...curr,
            key: curr.id || `suggestion-${index}`,
        });

        return acc;
    }, [] as Partial<Suggestion>[]);
};

export const WaypointInput = (props: WaypointInputProps) => {
    const intl = useIntl();

    const {
        waypoint,
        suggestedAddresses,
        onInputValueChanged,
        onSuggestionSelected,
        onInputCleared,
        onRemove,
        allowRemove,
        icon,
    } = props;

    const tour = useAppSelector(selectCurrentTour);
    const isAutomated = useAppSelector(selectIsAutomatedPlanningMode);
    const waypointId = waypoint.id;
    const orderedWaypoints = useAppSelector(selectOrderedWaypoints);
    const isFirstWaypoint = waypointId === first(orderedWaypoints)?.id;
    const isLastWaypoint = waypointId === last(orderedWaypoints)?.id;
    const isWaypointInRange = useAppSelector(state => selectIsWaypointInRange(state, waypointId));
    const waypointInRange = isWaypointInRange ?? true;

    const handleSuggestionsFetchRequested = (result: { value: string }) => {
        const { value } = result;
        if (waypoint.addressLabel !== value) {
            onInputValueChanged(waypoint.id, value);
        }
    };

    const handleSuggestionSelected = (_event: React.UIEvent, { suggestion }: SelectedSuggestion) => {
        onSuggestionSelected(waypoint.id, suggestion);
        if (suggestion.resultType === WaypointType.VEHICLE_LOCATION && suggestion.position) {
            changeBoundingBox([suggestion.position]);
        }
    };

    const renderSuggestion = (suggestion: AutoSuggestSuggestion) => {
        return (
            <span>
                <span className={`rioglyph rioglyph-${suggestion.type as string} margin-right-3`} />
                <span>{suggestion.label}</span>
            </span>
        );
    };

    const suggestions: Partial<Suggestion>[] = suggestedAddresses
        .filter(address => {
            if (isFirstWaypoint || isLastWaypoint) {
                return address.resultType !== 'charging-station';
            }
            return true;
        })
        .map(address => {
            return {
                ...address,
                label: address.title,
                type: mapSuggestionIconType(address.resultType),
            };
        });

    const suggestionsWithHeaders = addSuggestionHeaders(suggestions, intl) as AutoSuggestSuggestion[];

    const inputProps = {
        onClear: () => {
            onInputCleared(waypoint.id);
        },
        placeholder: intl.formatMessage({ id: 'intl-msg:smartRoute.tour.findAddress' }),
        icon: `rioglyph-${icon}`,
        value:
            validWaypointPredicate(waypoint) && isEmpty(waypoint.addressLabel)
                ? getFallbackText(waypoint)
                : waypoint.addressLabel,
        hasError: isWaypointInRange,
    } as AutoSuggestInputProps;

    const removeAddon = (
        <button
            key='remove-addon'
            type='button'
            className='input-group-addon'
            onMouseDown={onRemove}
            tabIndex={0}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRemove();
                }
            }}
        >
            <span className='rioglyph rioglyph-trash cursor-pointer text-color-gray hover-text-color-danger' />
        </button>
    );

    const handleTrailingInputAddons = () => (allowRemove ? [removeAddon] : []);

    const showUnreachableWarning = tour && !isAutomated && !isFirstWaypoint && !waypointInRange;

    return (
        <div className={`form-group ${showUnreachableWarning ? 'has-feedback has-warning' : 'margin-0'}`}>
            <AutoSuggest
                className='WaypointInput flex-1-0'
                dropdownClassName='max-height-350'
                inputProps={inputProps}
                suggestions={suggestionsWithHeaders}
                onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
                onSuggestionSelected={handleSuggestionSelected}
                renderSuggestion={renderSuggestion}
                trailingInputAddons={handleTrailingInputAddons()}
            />
            {showUnreachableWarning && (
                <div>
                    <span
                        className={`form-control-feedback 
                            ${orderedWaypoints.length > 2 ? ' margin-right-25' : 'margin-0'}`}
                    >
                        <span className='rioglyph rioglyph-warning-sign margin-right-5' />
                    </span>
                    <span className='help-block'>
                        <FormattedMessage id='intl-msg:smartRoute.vehicle.stopNotReachable' />
                    </span>
                </div>
            )}
        </div>
    );
};
